import React, { useEffect, useRef, useState } from 'react'
import _debounce from 'lodash.debounce'
import '../../styles.scss'
import './styles.scss'
import { apiInchat } from 'api'
import { CollapsibleContainer, InputChipsWithSearch } from 'components/common'
import ModalWrapper from 'components/ModalWrapper'
import { lang } from 'utils/helpers'
import { ModalCreateSection, UserCard } from '../../components'
import { useDispatch, useSelector } from 'react-redux'
import inChatRedux from 'redux/inchat/actions'
import ButtonPlus from '../../components/ButtonPlus'
import Options from './Options'
import { Draggable, Droppable } from 'react-beautiful-dnd'

const DirectMessagesContainer = ({ openCard }) => {
  const { inchat:{ direct: { conversations } } } = useSelector(state => state)
  const [isOpenCollapsible, setIsOpenCollapsible] = useState(true)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isOpenModalSection, setIsOpenModalSection] = useState(false)
  const isFirstRender = useRef(true)
  const [isHide, setIsHide] = useState(true)

  const [usersData, setUsersData] = useState({})
  const [selectedUsers, setSelectedUsers] = useState({})
  const [conversationsLoading, setConversationsLoading] = useState(false)
  const dispatch = useDispatch()
  const [modalLoading, setModalLoading] = useState(false)
  const [errorModalMsg, setErrorModalMsg] = useState('')
  const debouncedUsersSearch = useRef(
    _debounce(async text => {
      setUsersData(await getUsers(text))
    }, 300)
  ).current

  useEffect(() => {
    const modalContent = document.querySelector('#directMessages')
    if(isOpenCollapsible && modalContent){
      modalContent.addEventListener('scroll', handleScroll)
    }else{
      if(modalContent) modalContent.removeEventListener('scroll', handleScroll)
    }
    return () => {if(modalContent) modalContent.removeEventListener('scroll', handleScroll)}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenCollapsible])

  useEffect(() => {
    // No se ejecuta el efecto la primera vez
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if(isOpenCollapsible) {
      getConversationList()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenCollapsible])

  function handleScroll(event) {
    const scrollTop = event.target.scrollTop
    const scrollHeight = event.target.scrollHeight
    const clientHeight = event.target.clientHeight
    if (scrollTop + clientHeight >= scrollHeight) {
      handleNextPage()
    }
  }

  const toggleCollapsible = () => setIsOpenCollapsible(prevState => !prevState)
  const toggleModal = () => {
    if (isOpenModal) setSelectedUsers({})
    setIsOpenModal(prevState => !prevState)
  }

  const toggleModalSection = () => {
    setIsOpenModalSection(prevState => !prevState)
  }

  const getConversationList = async (nextPage = false) => {
    setConversationsLoading(true)
    await dispatch(inChatRedux.getDirectConversationsList(nextPage))
    setConversationsLoading(false)
  }

  const handleNextPage = () => {
    getConversationList(true)
  }

  const getUsers = async text => {
    const { data } = await apiInchat.searchUsers({ text })
    return data
  }

  const handleSelectedUsers = values => {
    setSelectedUsers(values)
    if (values.input) {
      debouncedUsersSearch(values.input)
    } else {
      setUsersData({})
    }
  }

  const handleCreate = () => {
    if (!selectedUsers.chips?.length) {
      setErrorModalMsg(lang('dashboard_add-least-one-user'))
      return
    }
    const userIds = selectedUsers.chips.map(user => user.id)
    setModalLoading(true)
    apiInchat.createConversation({
      type: userIds.length > 1 ? 'group' : 'direct',
      userIds
    })
      .then(() => {
        getConversationList()
        toggleModal()
      })
      .catch(() => setErrorModalMsg(lang('dashboard_conversation-not-created')))
      .finally(() => setModalLoading(false))
  }

  const handleOpenCard = (conversation) => {
    openCard(conversation._id)
  }

  return (
    <Droppable
      droppableId='message_direct'
      type={'CONVERSATIONS'}
      direction='vertical'
    >
      {(provider_direct, snapshot) => (
        <div
          className={`
            inchat-menu__container
            inchat-direct-messages-container
            ${snapshot.isDraggingOver ? 'inchat-direct-messages-container__dragging' : ''}
          `}
          ref={provider_direct.innerRef}
          {...provider_direct.droppableProps}
        >
          <CollapsibleContainer
            isOpen={isOpenCollapsible}
            toggle={toggleCollapsible}
            height={250}
            isLoading={conversationsLoading}
            title={lang('dashboard_direct-messages')}
            onChangeMouseOver={(value) => setIsHide(!value)}
            optionsComponent={
              <div className='inchat-menu__options-collapsible'>
                <Options
                  isHide={isHide}
                  toggleOpenModal={toggleModalSection}
                  setConversationsLoading={setConversationsLoading}
                />
                <ButtonPlus
                  onClick={toggleModal}
                  altText={lang('dashboard_add-direct-message')}
                />
              </div>
            }
            id='directMessages'
            className='inchat-direct-messages-container__collapsible'
          >
            <div
              className='inchat-direct-messages-container__conversation-list'
            >
              {conversations.map((conversation, index) => {
                return (
                  <Draggable
                    key={conversation._id}
                    draggableId={conversation._id}
                    index={index}
                    isDragDisabled={!conversation._id ? true : false}
                  >
                    {(provider_card)=>(
                      <div
                        ref={provider_card.innerRef}
                        style={{
                          ...provider_card.draggableProps.style
                        }}
                        {...provider_card.draggableProps}
                        {...provider_card.dragHandleProps}
                      >
                        <UserCard
                          key={conversation._id}
                          conversation={conversation}
                          onClick={handleOpenCard}
                        />
                      </div>
                    )}
                  </Draggable>
                )
              })}
              {provider_direct.placeholder}
            </div>
          </CollapsibleContainer>
          {/* Modal para añadir un nuevo mensaje directo */}
          <ModalWrapper
            isOpen={isOpenModal}
            toggle={toggleModal}
            header={lang('dashboard_new-conversation')}
            body={
              <div className='inchat-direct-messages-container__modal-container'>
                <label>{lang('dashboard_users')}</label>
                <InputChipsWithSearch
                  data={usersData.users?.map(user => ({
                    id: user._id,
                    avatar: user.picture,
                    name: `${user.name} ${user.lastname}`
                  }))}
                  placeholder={lang('dashboard_add-users')}
                  values={selectedUsers}
                  onChange={handleSelectedUsers}
                />
              </div>
            }
            eventBtnTxt={lang('dashboard_create')}
            eventBtn={handleCreate}
            cancelBtnTxt={lang('buttons_cancel')}
            cancelBtn={toggleModal}
            isSubmitting={modalLoading}
            error={errorModalMsg}
          />
          <ModalCreateSection
            isOpenModal={isOpenModalSection}
            toggleModal={toggleModalSection}
            dispatch={dispatch}
          />
        </div>
      )}
    </Droppable>
  )
}

export default DirectMessagesContainer